<template>
    <div class="itemSearchWrapper" :class="{'active': isSearchFocused}">
        <button class="toggleButton" @click="toggleSearch" v-if="isMobile"><icon class="fa-fw" icon="search"></icon></button>
        <div class="itemSearch" :class="{'active': isSearchFocused}">
            <button class="closeButton" @click="toggleSearch" v-if="isSearchFocused"><icon class="fa-fw" icon="times"></icon></button>
            <div class="searchContainer">
                <div class="position-relative">
                    <div class="d-flex flex-grow-1 position-relative searchInputBox">
                        <input type="search" class="search-input flex-grow-1 px-3 py-1" ref="searchInput" v-model="searchString" @input="onValueChanged($event.target.value)"
                            @keyup.enter="search()" @focus="isSearchFocused = true" @blur="onBlurSearchField($event)" :autofocus="isShopBuilder" :placeholder="$translate('Ceres::Template.headerSearchPlaceholder')" :aria-label="$translate('Ceres::Template.headerSearchTerm')">

                        <slot name="search-button">
                            <button class="search-submit px-3" type="submit" @click="search()" :aria-label="$translate('Ceres::Template.headerSearch')">
                                <icon class="fa-fw" icon="search" :loading="autocompleteIsLoading"></icon>
                            </button>
                        </slot>
                    </div>

                    <template v-if="isSearchFocused">
                        <div class="results" v-show="hasInitialInput">
                            <slot name="autocomplete-suggestions">
                                <div class="autocomplete-suggestions w-100">
                                    <search-suggestion-item
                                        suggestion-type="suggestion">
                                    </search-suggestion-item>
                                    <search-suggestion-item
                                        suggestion-type="category">
                                    </search-suggestion-item>
                                    <search-suggestion-item
                                        :show-images="true"
                                        :padding-classes="''"
                                        suggestion-type="item">
                                    </search-suggestion-item>
                                </div>
                            </slot>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import UrlService from "../../services/UrlService";
import { isNullOrUndefined, defaultValue } from "../../helper/utils";
import { pathnameEquals } from "../../helper/url";
import { mapState } from 'vuex';
import { debounce } from '../../helper/debounce';

export default {

    name: "item-search",

    props: {
        showItemImages:
        {
            type: Boolean
        },
        forwardToSingleItem:
        {
            type: Boolean,
            default: App.config.search.forwardToSingleItem
        },
        timeout:
        {
            type: Number,
            default: 200
        },
        searchMinLength:
        {
            type: Number,
            default: 2
        }
    },

    data()
    {
        return {
            isSearchFocused: false,
            onValueChanged: null,
            searchString: "",
            hasInitialInput: false,
            isMobile: false
        };
    },

    computed:
    {
        hasAutocompleteResults()
        {
            const item       = this.autocompleteResult.item;
            const category   = this.autocompleteResult.category;
            const suggestion = this.autocompleteResult.suggestion;

            return App.isShopBuilder || (item && item.length) || (category && category.length) || (suggestion && suggestion.length);
        },

        isShopBuilder()
        {
            return App.isShopBuilder;
        },

        ...mapState({
            autocompleteResult: state => state.itemSearch.autocompleteResult,
            moduleSearchString: state => state.itemList.searchString,
            autocompleteIsLoading: state => state.itemSearch.autocompleteIsLoading
        })
    },

    mounted()
    {
        this.onValueChanged = debounce(searchString =>
        {
            this.autocomplete(searchString);
        }, defaultValue(this.timeout, 200));

        this.isMobile = window.matchMedia("(max-width: 768px)").matches;

        this.$nextTick(() =>
        {
            const urlParams = UrlService.getUrlParams(document.location.search);

            this.$store.commit("setItemListSearchString", urlParams.query);

            this.$refs.searchInput.value = !isNullOrUndefined(urlParams.query) ? urlParams.query : "";
        });
    },

    methods:
    {
        search()
        {
            if (this.$refs.searchInput.value.length)
            {
                if (pathnameEquals(App.urls.search))
                {
                    this.$store.dispatch("searchItems", this.$refs.searchInput.value);
                }
                else
                {
                    window.open(`${App.urls.search}?query=${ this.searchString }`, "_self", false);
                }
            }
        },

        autocomplete(searchString)
        {
            if (searchString.length >= this.searchMinLength)
            {
                this.$store.dispatch("loadItemSearchAutocomplete", searchString);
            }
            else
            {
                this.$store.commit("setAutocompleteResult", { item: [], category: [], suggestion: [] });

                // hide the autocomplete box
                this.hasInitialInput = false;
            }
        },

        // hide search, if targetElement of the blur event is not a child of components' root element
        onBlurSearchField(event)
        {
            const target = event.relatedTarget;

            if (isNullOrUndefined(target) || !isNullOrUndefined(target) && !this.$el.contains(target))
            {
                this.isSearchFocused = false;
            } else {
                this.autocomplete(this.searchString);
            }
        },

        toggleSearch()
        {
            if(!this.isSearchFocused)
            {
                this.autocomplete(this.searchString);

                this.$refs.searchInput.focus();
                this.isSearchFocused = true;
                return;
            }
            this.isSearchFocused = false;
        }
    },

    watch:
    {
        // set the current search string, after clicking on a suggestion
        moduleSearchString(newVal)
        {
            if (newVal && newVal.length)
            {
                this.searchString = newVal;
            }
        },

        isSearchFocused(newVal)
        {
            if (newVal)
            {
                this.autocomplete(this.searchString);
                setTimeout(function()
                {
                    document.documentElement.classList.add("scroll-locked");
                }, 1);
            } else {
                setTimeout(function()
                {
                    document.documentElement.classList.remove("scroll-locked");
                }, 1);
            }
        },

        autocompleteIsLoading(newVal, oldVal) {
            // when client was loading and has stopped now, the autocomplete box should be shown
            if (oldVal && !newVal) {
                this.hasInitialInput = true;
            }
        }
    }
}
</script>
